import React from 'react';
import {DatePicker, DatePickerProps} from '@material-ui/pickers/DatePicker';
import MuiPickersUtilsProvider from '@material-ui/pickers/MuiPickersUtilsProvider';
import {createStyles, withStyles, MuiThemeProvider} from '@material-ui/core/styles';
import DateUtils from '@date-io/date-fns';
import ruLocale from 'date-fns/locale/ru';
import format from 'date-fns/format';
import {BaseFieldProps, BaseFieldVariant} from '../../index';
import {images} from '../../../../../assets/icons';
import {useIsDesktop} from '../../../../hooks/useIsDesktop';
import {useTheme} from '../../../../theme';
import {Theme} from '../../../../theme/interface';
import {dateFormats} from '../../../../constants/date';
import {useSelector} from 'react-redux';
import {themeSelector} from '../../../../../redux/selectors/runtime/theme';
import {VisImpStatus} from '../../../../models/visuallyImpairedSettings';

class LocalizedDateUtils extends DateUtils {
  getCalendarHeaderText(date: Date) {
    return format(date, 'LLLL yyyy', {locale: this.locale});
  }
}

export const BaseFieldDate: React.FC<BaseFieldProps> = (props) => {
  const {label, input, readOnly, variant} = props;
  const {minDate, maxDate, disableToolbar = true} = props as unknown as DatePickerProps;

  const theme = useTheme();
  const isDesktop = useIsDesktop();
  const themeSettings = useSelector(themeSelector());
  const visActive = themeSettings.visuallyImpaired === VisImpStatus.Active;

  const StyledDatePicker = React.useMemo(
    () => withStyles(createCustomStyles(theme, variant!, isDesktop, readOnly, visActive))(DatePicker),
    [theme, variant, isDesktop, readOnly, visActive],
  );

  return (
    <MuiPickersUtilsProvider utils={LocalizedDateUtils} locale={ruLocale}>
      <MuiThemeProvider theme={theme.muiDefaultTheme}>
        <StyledDatePicker
          format={dateFormats.inputDate}
          // cancelLabel='ОТМЕНА'
          // okLabel='ОК'
          invalidDateMessage='Недопустимый формат даты'
          minDateMessage='Дата не должна быть меньше минимальной даты'
          maxDateMessage='Дата не должна быть больше максимальной даты'
          variant='inline'
          onChange={(event) => input.onChange && input.onChange(event)}
          value={input.value}
          InputProps={{
            endAdornment: <images.Calendar color={theme.input.variants.outlined.borderColor} width={30} height={30} />,
          }}
          disableToolbar={disableToolbar}
          inputVariant={variant}
          readOnly={readOnly}
          label={label}
          minDate={minDate}
          maxDate={maxDate}
          autoOk
        />
      </MuiThemeProvider>
    </MuiPickersUtilsProvider>
  );
};

const createCustomStyles =
  (theme: Theme, variant: BaseFieldVariant, isDesktop: boolean, readOnly = false, visActive = false) =>
  () => {
    if (variant === 'outlined') {
      return createStyles({
        root: {
          backgroundColor: theme.color.white,
          borderRadius: '4px',
          overflow: 'hidden',
          '& .MuiOutlinedInput-input': {
            ...theme.fonts.body,
            lineHeight: isDesktop ? '28px' : '20px',
            padding: isDesktop ? '8px 10px' : '8px 6px',
            color: theme.input.variants.outlined.borderColor,
          },
          '& .MuiInputLabel-outlined:not(.Mui-focused):not(.MuiFormLabel-filled)': {
            transform: 'translate(14px, 14px) scale(1)',
          },
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderWidth: '1px !important',
              borderColor: visActive ? theme.color.primaryColor : 'transparent',
            },
          },
          '& .MuiOutlinedInput-root.Mui-focused, & .MuiOutlinedInput-root:hover': {
            '& fieldset': {
              borderWidth: '1px !important',
              borderColor: visActive ? theme.color.primaryColor : 'transparent !important',
            },
          },
          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.color.primaryColor,
          },
          '& .MuiFormLabel-root.Mui-focused': {
            color: theme.color.primaryColor,
          },
          ...(readOnly
            ? {
                '& .Mui-disabled': {
                  color: theme.color.textColor,
                },
              }
            : {}),
        },
      });
    }

    return createStyles({
      root: {
        '& .MuiInputBase-root': {
          color: theme.color.textColor,
        },
        '& .MuiInput-underline:before': {
          borderColor: theme.input.variants.outlined.borderColor,
        },
        '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
          borderColor: theme.input.variants.outlined.borderColor,
        },
      },
    });
  };
